import type { ReactNode } from "react";
import InputText from "@/shared/ui/components/input-text";
import Banner, { BannerType } from "@/shared/ui/components/banner";
import { useTranslation } from "react-i18next";
import { cardIconByScheme } from "@/shared/ui/components/card-icon";
import type { CheckoutSession } from "@/types";
import { PageError } from "../types";
import { emptySession } from "@/lib/checkout";
import { ShieldIcon } from "@/shared/ui/icons/shield-icon";
import { BackArrowIcon } from "@/shared/ui/icons/back-arrow-icon";
import { ForwardArrowIcon } from "@/shared/ui/icons/forward-arrow-icon";

interface InlineCardFormProps {
  cardForm: {
    cardNumber: string;
    expiryDate: string;
    cvv: string;
    cardholderName: string;
    saveCard: boolean;
  };
  onCardFormChange: (form: InlineCardFormProps["cardForm"]) => void;
  error: PageError;
  session?: CheckoutSession;
  rtl?: boolean;
  onBack?: () => void;
  /** Single page checkout already renders a "Payment method" section heading,
   *  so the form suppresses its own "Enter card details" heading to avoid a
   *  redundant double heading. */
  hideHeading?: boolean;
  /**
   * Optional content rendered directly under the form heading (or at the top
   * of the form when the heading is hidden). Used by PNM no-card hierarchy so
   * the verification message sits under "Enter card details", above fields.
   */
  afterHeading?: ReactNode;
}

/**
 * Component for inline card form (not in modal)
 * Single responsibility: Rendering inline card form UI
 */
export function InlineCardForm({
  cardForm,
  onCardFormChange,
  error,
  session,
  rtl = false,
  onBack,
  hideHeading = false,
  afterHeading,
}: InlineCardFormProps) {
  const { t } = useTranslation();
  const displaySession = session ?? emptySession;

  return (
    <div className="space-y-4" data-testid="inline-card-form">
      {onBack && (
        <button
          onClick={onBack}
          className={`flex items-center gap-2 mb-4 text-[#16181D] hover:opacity-70 transition-opacity ${
            rtl ? "flex-row-reverse" : ""
          }`}
          type="button"
          data-testid="inline-card-form-back-button"
        >
          {rtl ? <ForwardArrowIcon /> : <BackArrowIcon />}
        </button>
      )}

      {!hideHeading && (
        <div className="mb-4">
          <h3 className="text-[16px] font-semibold text-[#16181D]">
            {t("payment_confirmation.enter_card_details")}
          </h3>
        </div>
      )}
      {afterHeading}
      {error === PageError.TOKENIZATION_FAILED && (
        <Banner type={BannerType.ERROR} data-testid="inline-card-form-error-banner">
          {t("payment_confirmation.tokenization_error")}
        </Banner>
      )}

      <div className="space-y-8">
        <div>
          <label className="block text-sm font-medium text-[#16181D] mb-2">
            {t("payment_confirmation.card_number")}
          </label>
          <InputText
            type="text"
            placeholder={t("payment_confirmation.card_number_placeholder")}
            value={cardForm.cardNumber}
            onChange={(e) =>
              onCardFormChange({
                ...cardForm,
                cardNumber: e.target.value,
              })
            }
            mask="9999 9999 9999 9999"
            strict={/^[0-9]*$/}
            maxLength={19}
            data-testid="inline-card-number-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#16181D] mb-2">
              {t("payment_confirmation.expiry_date")}
            </label>
            <InputText
              type="text"
              placeholder={t("payment_confirmation.expiry_placeholder")}
              value={cardForm.expiryDate}
              onChange={(e) =>
                onCardFormChange({
                  ...cardForm,
                  expiryDate: e.target.value,
                })
              }
              mask="99/99"
              strict={/^[0-9]*$/}
              maxLength={5}
              data-testid="inline-card-expiry-input"
            />
          </div>
          <div>
            <label className="block text-[#16181D] text-sm font-medium mb-2">
              {t("payment_confirmation.cvv")}
            </label>
            <InputText
              type="text"
              placeholder={t("payment_confirmation.cvv_placeholder")}
              value={cardForm.cvv}
              onChange={(e) =>
                onCardFormChange({ ...cardForm, cvv: e.target.value })
              }
              strict={/^[0-9]*$/}
              maxLength={4}
              data-testid="inline-card-cvv-input"
            />
          </div>
        </div>
        
      </div>

      <div className="my-6">
        <h4 className="text-[#16181D] text-sm font-medium mb-3">
          {t("payment_confirmation.we_accept")}
        </h4>
        <div className="flex gap-1.5" data-testid="accepted-card-schemes">
          {(displaySession.country.cardsAllowed ?? []).map((scheme, i) => (
            <div key={`card_icon_${i}`}>{cardIconByScheme(scheme)}</div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 items-center bg-[#F4F6F9] p-2 rounded-md my-6" data-testid="secure-card-details-notice">
        <div>
          <ShieldIcon color="green" />
        </div>
        <div className="text-xs text-[#585B7A]">
          {t("payment.secure_card_details")}
        </div>
      </div>
    </div>
  );
}
